import { useState } from "react";
import Selector from "../../Selectors/Selector";
import { useLeadsStore } from "../../../stores/LeadsStore";
import {
  IconCheck,
  IconPlayerPause,
  IconPlayerPlay,
  IconPlayerStop,
} from "@tabler/icons-react";
import { useLanguage } from "../../../stores/LanguageStore";
import { Group, TextInput, Tooltip } from "@mantine/core";
import AddOption from "../../Adder/AddOption";

function extractValuesByField(items, fieldName) {
  const result = [];

  for (const item of items) {
    if (item.field_name === fieldName) {
      result.push(item.value);
    }

    // Check if there's a nested 'values' array (e.g., for 'mode' groups)
    if (Array.isArray(item.values)) {
      result.push(...extractValuesByField(item.values, fieldName));
    }
  }

  return result;
}

const FilterLazyValue = ({
  field_name,
  label,
  setValue,
  value,
  getter,
  responseField,
  storeKey,
  setStoreKey,
  nameField,
  groups,
  filterItems,
  handleResponse,
  free,
  isMobile,
  readOnly,
  nullable
}) => {
  const lang = useLanguage((s) => s.language);
  const options = useLeadsStore((s) => s[storeKey]);
  const setOptions = useLeadsStore((s) => s[setStoreKey]);
  const [loading, setLoading] = useState(false);

  const alreadyOnOtherItems = extractValuesByField(filterItems, field_name);

  const getOptions = async () => {
    setLoading(true);
    if (groups) {
      let data = [];
      for (const group of groups) {
        let response = await group.getter();
        if (response) {
          data.push({
            label: group.name,
            options: response.data[group.responseField].map((x) => ({
              ...x,
              option_name: typeof x === "string" ? x : x[nameField] || "-",
            })),
          });
        }
      }

      setLoading(false);
      setOptions(data);
    } else
      getter()
        .then((response) => {
          setOptions([
            {
              label,
              options: handleResponse
                ? handleResponse(response.data[responseField])
                : response.data[responseField]
                    ?.sort((a, b) => b.id - a.id)
                    ?.map((x) => ({
                      ...x,
                      option_name: typeof x === "string" ? x : x[nameField],
                    })) || [],
            },
          ]);
        })
        .finally(() => setLoading(false));
  };

  if (free) {
    return (
      <TextInput
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={lang.table.toolbar.filter.values.value_placeholder + " *"}
        required={nullable}
        autoFocus
        w={isMobile ? "100%" : 250}
        rightSection={
          <AddOption
            handleAdd={(v) => setValue(v.id.toString())}
            loading={loading}
            getter={getter}
            handleResponse={handleResponse}
            responseField={responseField}
            items={options[0]?.options || []}
            alreadyOnOtherItems={alreadyOnOtherItems}
          />
        }
      />
    );
  }

  return (
    <Selector
      options={options}
      hasGroup
      placeholder={label}
      loading={loading}
      value={value === "" ? null : handleResponse ? value : parseInt(value)}
      onChange={(v) => setValue(v.id.toString())}
      required={!nullable}
      size={"sm"}
      getOptionName={(o) => {
        return handleResponse ? o["option_name"] : o[nameField] + " #" + o.id;
      }}
      onDropdownOpen={getOptions}
      rightOptionSection={(item) => {
        return (
          <>
            {alreadyOnOtherItems?.includes(item?.id?.toString()) && (
              <IconCheck size={18} />
            )}
          </>
        );
      }}
      bottomOptionSection={(item) => {
        if (field_name === "programmation_id") {
          return (
            <Group w={"100%"}>
              {item?.id && (
                <span style={{ fontSize: 10, opacity: 0.35 }}>#{item?.id}</span>
              )}
              {item?.stopped || item?.archived ? (
                <Tooltip
                  label={`${lang.statistics.filters.paused}${
                    item?.archived
                      ? ` - ${lang.workflows.program.step_exclusion.columns.archived.true}`
                      : ""
                  }`}
                >
                  <IconPlayerStop
                    style={{ marginLeft: "auto" }}
                    size={12}
                    color={item?.archived ? "purple" : "red"}
                  />
                </Tooltip>
              ) : (
                <Tooltip label={lang.statistics.filters.running}>
                  <IconPlayerPlay
                    size={12}
                    color="green"
                    style={{ marginLeft: "auto" }}
                  />
                </Tooltip>
              )}
            </Group>
          );
        }
        return (
          item?.id &&
          !handleResponse && (
            <span style={{ fontSize: 8, opacity: 0.35 }}>#{item?.id}</span>
          )
        );
      }}
      unclearable
      readOnly={readOnly}
    />
  );
};

export default FilterLazyValue;
